<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import type { AppPageProps } from '@/types';
import { usePage } from '@inertiajs/vue3';
import { Check, Copy, Mail, Phone } from 'lucide-vue-next';
import { ref, type Ref } from 'vue';

const { t } = useTranslations();
const page = usePage<AppPageProps>();
const phoneNumber = page.props.contact.phone;
const email = page.props.contact.email;

const phoneCopied: Ref<boolean> = ref(false);
const emailCopied: Ref<boolean> = ref(false);

const copyToClipboard = async (text: string, copiedRef: Ref<boolean>) => {
    try {
        await navigator.clipboard.writeText(text);
        copiedRef.value = true;
        setTimeout(() => {
            copiedRef.value = false;
        }, 2000);
    } catch (err) {
        console.error('Failed to copy:', err);
    }
};

const copyPhone = () => copyToClipboard(phoneNumber, phoneCopied);
const copyEmail = () => copyToClipboard(email, emailCopied);
</script>

<template>
    <div class="fixed top-1/2 right-0 z-50 flex -translate-y-1/2 flex-col items-end gap-2 p-2 md:gap-3 md:p-0">
        <!-- Phone Button -->
        <div
            class="group flex w-max translate-x-[calc(100%-3rem)] items-center transition-transform duration-300 ease-out focus-within:translate-x-0 hover:translate-x-0 md:translate-x-[calc(100%-4.5rem)]"
        >
            <div class="flex items-center gap-2 rounded-l-lg bg-white p-2 shadow-lg md:gap-3 md:p-3">
                <a
                    :href="`https://wa.me/${phoneNumber.replace(/\+/g, '').replace(/\s/g, '')}`"
                    class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-dark text-white transition-colors hover:bg-primary md:h-12 md:w-12"
                    :title="phoneNumber"
                >
                    <Phone :size="20" class="md:!h-6 md:!w-6" />
                </a>
                <div
                    class="hidden items-center gap-2 overflow-hidden opacity-0 transition-opacity duration-300 group-focus-within:opacity-100 group-hover:opacity-100 md:flex"
                >
                    <span class="text-sm font-medium whitespace-nowrap text-dark">{{
                        phoneNumber.replace(/^(\+\d{2})(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1 $2 $3 $4 $5 $6')
                    }}</span>
                    <button
                        type="button"
                        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                        :title="phoneCopied ? t('contactButtons.copied') : t('contactButtons.copyPhone')"
                        @click.stop="copyPhone"
                        tabindex="0"
                    >
                        <Check v-if="phoneCopied" :size="18" class="text-green-600" />
                        <Copy v-else :size="18" class="text-gray-600" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Email Button -->
        <div
            class="group flex w-max translate-x-[calc(100%-3rem)] items-center transition-transform duration-300 ease-out focus-within:translate-x-0 hover:translate-x-0 md:translate-x-[calc(100%-4.5rem)]"
        >
            <div class="flex items-center gap-2 rounded-l-lg bg-white p-2 shadow-lg md:gap-3 md:p-3">
                <a
                    :href="`mailto:${email}`"
                    class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-dark text-white transition-colors hover:bg-primary md:h-12 md:w-12"
                    :title="email"
                >
                    <Mail :size="20" class="md:!h-6 md:!w-6" />
                </a>
                <div
                    class="hidden items-center gap-2 overflow-hidden opacity-0 transition-opacity duration-300 group-focus-within:opacity-100 group-hover:opacity-100 md:flex"
                >
                    <span class="text-sm font-medium whitespace-nowrap text-dark">{{ email }}</span>
                    <button
                        type="button"
                        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                        :title="emailCopied ? t('contactButtons.copied') : t('contactButtons.copyEmail')"
                        @click.stop="copyEmail"
                        tabindex="0"
                    >
                        <Check v-if="emailCopied" :size="18" class="text-green-600" />
                        <Copy v-else :size="18" class="text-gray-600" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
